// frontend/src/components/HelpTooltip.tsx - Reusable Help Tooltip Component

import React, { useState } from 'react';

interface HelpTooltipProps {
  content: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  maxWidth?: string;
}

const HelpTooltip: React.FC<HelpTooltipProps> = ({ 
  content, 
  position = 'top', 
  maxWidth = '300px' 
}) => {
  const [isVisible, setIsVisible] = useState(false);

  const getTooltipPosition = () => {
    switch (position) {
      case 'bottom':
        return {
          top: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          marginTop: '8px'
        };
      case 'left':
        return {
          top: '50%',
          right: '100%',
          transform: 'translateY(-50%)',
          marginRight: '8px'
        };
      case 'right':
        return {
          top: '50%',
          left: '100%',
          transform: 'translateY(-50%)',
          marginLeft: '8px'
        };
      default: // top
        return {
          bottom: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          marginBottom: '8px'
        };
    }
  };

  const getArrowPosition = () => {
    switch (position) {
      case 'bottom':
        return {
          top: '-6px',
          left: '50%',
          transform: 'translateX(-50%)',
          borderBottom: '6px solid #374151',
          borderLeft: '6px solid transparent',
          borderRight: '6px solid transparent'
        };
      case 'left':
        return {
          top: '50%',
          right: '-6px',
          transform: 'translateY(-50%)',
          borderLeft: '6px solid #374151',
          borderTop: '6px solid transparent',
          borderBottom: '6px solid transparent'
        };
      case 'right':
        return {
          top: '50%',
          left: '-6px',
          transform: 'translateY(-50%)',
          borderRight: '6px solid #374151',
          borderTop: '6px solid transparent',
          borderBottom: '6px solid transparent'
        };
      default: // top
        return {
          bottom: '-6px',
          left: '50%',
          transform: 'translateX(-50%)',
          borderTop: '6px solid #374151',
          borderLeft: '6px solid transparent',
          borderRight: '6px solid transparent'
        };
    }
  };

  const styles = {
    container: {
      position: 'relative' as const,
      display: 'inline-block'
    },
    trigger: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '18px',
      height: '18px',
      borderRadius: '50%',
      backgroundColor: '#6b7280',
      color: 'white',
      fontSize: '12px',
      fontWeight: '600',
      cursor: 'help',
      border: 'none',
      outline: 'none',
      marginLeft: '6px',
      transition: 'all 0.2s ease',
      userSelect: 'none' as const
    },
    triggerHover: {
      backgroundColor: '#374151',
      transform: 'scale(1.1)'
    },
    tooltip: {
      position: 'absolute' as const,
      ...getTooltipPosition(),
      backgroundColor: '#374151',
      color: 'white',
      padding: '12px 16px',
      borderRadius: '8px',
      fontSize: '14px',
      lineHeight: '1.4',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      zIndex: 1000,
      maxWidth,
      textAlign: 'left' as const,
      opacity: isVisible ? 1 : 0,
      visibility: isVisible ? 'visible' as const : 'hidden' as const,
      transition: 'opacity 0.2s ease, visibility 0.2s ease',
      whiteSpace: 'normal' as const,
      wordWrap: 'break-word' as const
    },
    arrow: {
      position: 'absolute' as const,
      ...getArrowPosition(),
      width: 0,
      height: 0,
      border: 'solid transparent'
    }
  };

  return (
    <div style={styles.container}>
      <button
        type="button"
        style={{
          ...styles.trigger,
          ...(isVisible ? styles.triggerHover : {})
        }}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        onFocus={() => setIsVisible(true)}
        onBlur={() => setIsVisible(false)}
        aria-label="Help information"
        title={content} // Fallback for accessibility
      >
        ?
      </button>
      
      <div style={styles.tooltip}>
        <div style={styles.arrow}></div>
        {content}
      </div>
    </div>
  );
};

export default HelpTooltip;